import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Params, Router } from '@angular/router';
import { InstanceService } from '@commonspirit/ngx-instance';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { FalUrlBuilder } from '../../../core/util/url/fal-url-builder';
import { getCityAndStateSegment } from '../../../core/util/url/url-helpers';
import * as fromSearch from '../../../features/search/+state';
import { FacetSummary, FiltersModel } from '../../models/filters';
import { SelectedGeolocation, SessionConstants } from '../../models/v2/search';
import { GeolocationService } from '../../services/geolocation/geolocation.service';
import { EnvironmentService } from '@commonspirit/ngx-environment';
import { EnvValue } from '@commonspirit/ngx-environment/lib/models/key-value-pair';

@Component({
  selector: 'fal-combined-search-box',
  templateUrl: './combined-search-box.component.html',
  styleUrls: ['./combined-search-box.component.scss']
})
export class CombinedSearchBoxComponent implements OnInit {
  @Input() keyword = '';
  @Input() selectedGeolocation: SelectedGeolocation | null;
  @Input() filtersModel: FiltersModel = new FiltersModel(this.instanceService);
  @Input() falBaseUrl = '';
  @Input() geoSearchTerm = '';
  @Input() showLandingFilters = true;
  @Output() geoSearchHandle = new EventEmitter<string>();
  @Output() keywordHandle = new EventEmitter<string>();
  searchRadiusFilterDisplayText = '';
  showSearchRadiusFilter = false;
  defaultSearchDistance: number | undefined = 0;
  searchDistance = 0;
  showServicesAvailableFilter = false;
  servicesAvailableFilterDisplayText = '';
  showLocationTypeFilter = false;
  locationTypeFilterDisplayText = '';
  filtersKey = ConfigurationKey.Filters;
  searchRadiusKey = ConfigurationKey.SearchRadiusFilter;
  selectedLocations: string | undefined;
  selectedSpecialties: string | undefined;
  isLocationSelected = false;
  isValidSearchGeolocation = true;
  @Input() isButtonDisabled = true;

  searchDescription: any;
  defaultShowSearchButton: true;
  hideLocationSuggestions = false;
  hideKeywordSuggestions = false;
  showSearchBtn: boolean;
  defaultSearchButton = '';
  showRoundBtn: string;
  landingFilterConfig = ConfigurationKey.LandingFilters;
  landingFilters: any = [];
  specialtyLandingFilter: any = [];
  locationTypeLandingFilter: any = [];
  selectedLocation: SelectedGeolocation | null;
  enteredLocation = '';
  selectedKeyword: string | null;
  enteredKeyword = '';
  showError = false;
  serviceTypeSelected = false;
  enableBlockIT = false;

  constructor(
    private store: Store,
    private router: Router,
    private urlBuilder: FalUrlBuilder,
    public instanceService: InstanceService,
    public geoLocationService: GeolocationService,
    public environmentService: EnvironmentService
  ) {
    // intentionally empty
    if (!window.location.href.includes('distance')) window.sessionStorage.removeItem(SessionConstants.SelectedRadiusFilter);
    const enableBlockITForEnvironment: EnvValue | undefined = this.environmentService.getSetting('enableBlockIT')?.value;
    if (typeof enableBlockITForEnvironment === 'boolean') {
      this.enableBlockIT = enableBlockITForEnvironment;
    }
  }

  ngOnInit(): void {
    if (this.showLandingFilters) {
      this.setFilters();
    }
    this.searchDescription = this.instanceService.getConfiguration(ConfigurationKey.ShowSearchButton)?.value;
    let landingFilters =
      (this.instanceService.getConfiguration(this.landingFilterConfig)?.value as {
        label?: string;
        enabled: boolean;
        value?: string;
        type?: string;
        enableBlockIT?: boolean;
      }[]) ?? [];
    landingFilters = landingFilters.filter((x) => x.enableBlockIT === this.enableBlockIT);
    console.log(landingFilters);
    landingFilters.forEach((element) => {
      if (element.type === 'Speciality' && element.enabled === true) {
        this.specialtyLandingFilter.push(element);
      }
      if (element.type === 'LocationType' && element.enabled === true) {
        this.locationTypeLandingFilter.push(element);
      }
    });
    this.showSearchBtn = this.showSearchButton();
    this.showRoundBtn = this.getSearchButton();
  }

  showSearchButton(): boolean {
    return this.searchDescription?.iconButton ?? this.defaultShowSearchButton;
  }

  getSearchButton(): string {
    if (this.searchDescription?.enabled && this.searchDescription?.text) {
      return this.searchDescription?.text;
    }
    return this.defaultSearchButton;
  }

  async keywordEnterHandler(val: string): Promise<void> {
    if (this.isValidSearch()) {
      this.keyword = val.trim();
      await this.search();
    }
  }

  async locationEnterHandler(): Promise<void> {
    if (this.isValidSearch()) await this.search();
  }

  async search(): Promise<boolean> {
    sessionStorage.setItem(SessionConstants.ReplaceKeyword, this.keyword);
    if (this.selectedGeolocation?.name != '' && this.geoSearchTerm.trim() === '') this.geoSearchTerm = this.selectedGeolocation?.name ?? '';
    this.geoSearchHandle.emit(this.geoSearchTerm);
    this.keywordHandle.emit(this.keyword);
    const geolocationForRoute =
      this.geoSearchTerm &&
      this.selectedGeolocation &&
      this.selectedGeolocation.isLocationValid &&
      this.geoSearchTermMatchesSelectedGeolocation()
        ? this.selectedGeolocation
        : this.geoSearchTerm
        ? ({ name: this.geoSearchTerm } as SelectedGeolocation)
        : null;

    let geolocationFromQuery = null;

    if (geolocationForRoute != null) {
      geolocationFromQuery = await this.geoLocationService.queryLocation(geolocationForRoute.name).toPromise();
    }

    const keywordString = sessionStorage.getItem(SessionConstants.SelectedSuggestion);
    const keywordValue = keywordString ? JSON.parse(keywordString) : { term: '', isService: false };

    if (geolocationForRoute === null || geolocationFromQuery?.isLocationValid) {
      sessionStorage.setItem(SessionConstants.LocationFromRoute, JSON.stringify(geolocationFromQuery));
      const url = this.urlBuilder.searchBuild({
        isService: this.selectedKeyword ? this.serviceTypeSelected : (keywordValue?.isService as boolean),
        searchTerm:
          this.selectedKeyword ??
          this.keyword
            ?.trim()
            .replace(/[^A-Z0-9]+/gi, ' ')
            .trimEnd(),
        selectedGeolocation: geolocationForRoute
      });

      if (this.keyword) {
        sessionStorage.setItem(
          SessionConstants.SelectedSuggestion,
          JSON.stringify({
            term: this.keyword,
            isService: this.selectedKeyword ? this.serviceTypeSelected : (keywordValue?.isService as boolean) ?? false
          })
        );
      }

      this.isValidSearchGeolocation = true;
      // eslint-disable-next-line no-empty-pattern
      const queryParams: Params | null | undefined = ({} = this.setQueryParam());
      sessionStorage.setItem('searchUrl', url);
      return await this.router.navigate([url], { queryParams });
    } else {
      const selectedNonValidLocation = { isLocationValid: false, name: this.geoSearchTerm } as SelectedGeolocation;
      sessionStorage.setItem(SessionConstants.LocationFromRoute, JSON.stringify(selectedNonValidLocation));
      this.isValidSearchGeolocation = false;
      return false;
    }
  }
  getFilterSearch(key: string): string[] {
    const value = window.sessionStorage.getItem(key) as string;
    return value ? JSON.parse(value) : [];
  }

  setQueryParam(): Params {
    const queryParams: Params | null | undefined = {};

    const searchRadius =
      (this.instanceService.getConfiguration(this.searchRadiusKey)?.value as {
        displayText?: string;
        enabled: boolean;
        defaultDistance?: number;
      }[]) ?? (undefined as unknown);

    if (
      this.geoSearchTerm != '' &&
      window.sessionStorage.getItem(SessionConstants.SelectedRadiusFilter) != null &&
      window.sessionStorage.getItem(SessionConstants.SelectedRadiusFilter) != ''
    ) {
      this.searchDistance = parseInt(
        window.sessionStorage.getItem(SessionConstants.SelectedRadiusFilter) ?? String(searchRadius[0].defaultDistance)
      );
    } else {
      this.searchDistance = this.searchDistance ?? searchRadius[0].defaultDistance;
    }

    queryParams.latitude = this.selectedGeolocation?.latitude;
    queryParams.longitude = this.selectedGeolocation?.longitude;
    queryParams.distance = this.searchDistance == 0 && searchRadius ? searchRadius[0].defaultDistance : this.searchDistance;

    const locationList = this.getFilterSearch(SessionConstants.SelectedLocationTypesFilter);

    if (locationList && locationList.length > 0) {
      queryParams.locationType = locationList.join('|');
    }
    const specialtiesList = this.getFilterSearch(SessionConstants.SelectedSpecialtiesFilter);

    if (specialtiesList && specialtiesList.length > 0) {
      queryParams.specialties = specialtiesList.join('|');
    }
    return queryParams;
  }

  setGeolocation(selectedGeolocation: SelectedGeolocation): void {
    this.selectedLocation = selectedGeolocation;
    this.selectedGeolocation = selectedGeolocation;
    this.isLocationSelected = this.selectedGeolocation.isLocationValid;
    this.isButtonDisabled = !this.isValidSearch();
    if (selectedGeolocation != null) {
      this.store.dispatch(fromSearch.SearchActions.setSelectedGeolocation({ selectedGeolocation }));
    } else {
      this.store.dispatch(fromSearch.SearchActions.setSelectedGeolocation({ selectedGeolocation: null }));
      const searchRadius =
        (this.instanceService.getConfiguration(this.searchRadiusKey)?.value as {
          displayText?: string;
          enabled: boolean;
          defaultDistance?: number;
        }[]) ?? (undefined as unknown);
      this.filtersModel.searchRadius = searchRadius[0].defaultDistance;
    }
  }

  async handleFilterChange(event: any): Promise<void> {
    if (event.selectedSpecialties.length > 0 && event.servicesAvailableApplied) {
      this.selectedSpecialties = event.selectedSpecialties.map((x: FacetSummary) => x.name).join('|');
      this.servicesAvailableFilterDisplayText =
        event.selectedSpecialties.length > 1 ? `${event.selectedSpecialties.length} Services Available` : event.selectedSpecialties[0].name;
    } else {
      this.servicesAvailableFilterDisplayText = this.filtersModel.servicesAvailableFilterDisplayText;
    }
    if (event.selectedLocationTypes.length > 0 && event.locationTypeApplied) {
      this.selectedLocations = event.selectedLocationTypes.map((x: FacetSummary) => x.name).join('|');
      this.locationTypeFilterDisplayText =
        event.selectedLocationTypes.length > 1
          ? `${event.selectedLocationTypes.length} Location types`
          : event.selectedLocationTypes[0].name;
    } else {
      this.locationTypeFilterDisplayText = this.filtersModel.locationTypeFilterDisplayText;
    }

    if (event.locationSearchRadius !== this.defaultSearchDistance) {
      this.searchDistance = event.locationSearchRadius;
      this.searchRadiusFilterDisplayText =
        event.locationSearchRadius == 1 ? `${event.locationSearchRadius} Mile` : `${event.locationSearchRadius} Miles`;
    } else {
      this.searchRadiusFilterDisplayText = `${this.defaultSearchDistance} Miles`;
      this.searchDistance = event.locationSearchRadius;
    }
  }

  setFilters(): void {
    const filters =
      (this.instanceService.getConfiguration(this.filtersKey)?.value as {
        displayText?: string;
        enabled: boolean;
        defaultDistance?: number;
      }[]) ?? (undefined as unknown);
    if (filters) {
      for (const filter of filters) {
        switch (filter?.displayText?.toLowerCase()) {
          case 'search radius': {
            this.searchRadiusFilterDisplayText = `${filter?.defaultDistance} Miles`;
            this.showSearchRadiusFilter = filter.enabled;
            this.defaultSearchDistance = filter.defaultDistance;
            break;
          }
          case 'location type': {
            const locationTypes =
              (this.instanceService.getConfiguration(ConfigurationKey.LocationTypes)?.value as {
                item?: [{ value: string; label: string }];
              }) ?? (undefined as unknown);
            this.locationTypeFilterDisplayText = filter.displayText;
            this.showLocationTypeFilter = filter.enabled;
            this.filtersModel.availableLocationTypes = [];
            locationTypes.item?.forEach((x) => {
              this.filtersModel.availableLocationTypes.push({
                name: x.value,
                selected: false,
                label: x.label
              });
            });
            break;
          }
          case 'services available': {
            const specialties =
              (this.instanceService.getConfiguration(ConfigurationKey.Specialities)?.value as {
                item?: [{ value: string; label: string }];
              }) ?? (undefined as unknown);
            this.servicesAvailableFilterDisplayText = filter.displayText;
            this.showServicesAvailableFilter = filter.enabled;
            this.filtersModel.availableSpecialties = [];
            specialties.item?.forEach((x) => {
              this.filtersModel.availableSpecialties.push({
                name: x.value,
                selected: false,
                label: x.label
              });
            });
            break;
          }
          default:
            break;
        }
      }
    }
  }

  geoSearchTermChanged(geoSearchTerm: string): void {
    this.enteredLocation = geoSearchTerm;
    this.selectedLocation = null;
    this.geoSearchTerm = geoSearchTerm;
    if (this.geoSearchTerm === '') {
      this.selectedGeolocation = null;
      this.showSearchRadiusFilter = false;
      const searchRadius =
        (this.instanceService.getConfiguration(this.searchRadiusKey)?.value as {
          displayText?: string;
          enabled: boolean;
          defaultDistance?: number;
        }[]) ?? (undefined as unknown);
      this.searchDistance = searchRadius[0].defaultDistance ?? 10;
      this.store.dispatch(fromSearch.SearchActions.setSelectedGeolocation({ selectedGeolocation: null }));
    } else {
      this.showSearchRadiusFilter = true;
    }
    this.isButtonDisabled = !this.isValidSearch();
  }

  keywordSearchTermChanged(keyword: string): void {
    this.enteredKeyword = keyword;
    this.selectedKeyword = null;
    this.keyword = keyword;
    this.isButtonDisabled = !this.isValidSearch();
  }

  keywordsearchTermSelected(suggestionSelected: { id: string; name: string; isService: boolean }): void {
    this.selectedKeyword = suggestionSelected.id;
    this.enteredKeyword = '';
    this.keyword = suggestionSelected.name;
    this.serviceTypeSelected = suggestionSelected.isService;
    this.isButtonDisabled = !this.isValidSearch();
  }

  geoSearchTermMatchesSelectedGeolocation(): boolean {
    return getCityAndStateSegment({ name: this.geoSearchTerm } as SelectedGeolocation) === getCityAndStateSegment(this.selectedGeolocation);
  }

  async landingFilterClicked(val: string, type: string) {
    const queryParams: Params | null | undefined = {};
    const url = this.urlBuilder.searchBuild({
      isService: false,
      searchTerm: null,
      selectedGeolocation: null
    });

    if (type.toLowerCase() === 'speciality') {
      queryParams.specialties = val;
    } else {
      queryParams.locationType = val;
    }
    return await this.router.navigate([url], { queryParams });
  }

  settKeywordSearch(): void {
    this.hideKeywordSuggestions = !this.hideKeywordSuggestions;
  }

  setLocationSearchActive(): void {
    this.hideLocationSuggestions = false;
  }

  closeLocationSuggestion(): void {
    this.hideLocationSuggestions = true;
  }

  isValidSearch(): boolean {
    if (!this.selectedLocation && !this.selectedKeyword && !this.keyword && !this.selectedGeolocation) return false;
    const isLocationValid = (!!this.selectedLocation && this.enteredLocation != '') || this.enteredLocation === '';
    const isKeywordValid = (!!this.selectedKeyword && this.enteredKeyword != '') || this.enteredKeyword === '';
    const isMixedValid = (isLocationValid && this.enteredKeyword === '') || (isKeywordValid && this.enteredLocation === '');

    return isLocationValid && isKeywordValid && isMixedValid;
  }
}
